import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppMetaStore } from './appMeta';
import { apiUrl } from '../lib/apiBase';

const STORAGE_KEY = 'pwa_client_token';

export type ClientCustomerPayload = {
    id: string;
    phone: string;
    name?: string | null;
    email?: string | null;
    birth_date?: string | null;
    has_password?: boolean;
};

export type RegisterPayload = {
    name: string;
    phone: string;
    email: string;
    password: string;
    birth_date?: string | null;
};

export type AuthErrorPayload = {
    message?: string;
    errors?: Record<string, string[]>;
};

export type VerifyOtpExtras = {
    name?: string;
    email?: string | null;
};

type SendOtpOptions = {
    /** Mensagem quando o POST falhar (banner). */
    errorMessageSend?: string;
    /** Email opcional para receber o OTP enquanto o canal WhatsApp não está pronto. */
    email?: string | null;
};

async function apiJson<T>(
    method: string,
    url: string,
    body?: unknown,
    token?: string,
): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const errPayload = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw Object.assign(new Error((errPayload as { message?: string }).message ?? 'Request failed'), {
            status: res.status,
            data: errPayload as Record<string, unknown>,
        });
    }
    return errPayload as T;
}

export const useClientAuthStore = defineStore('client-auth', () => {
    const token = ref<string | null>(localStorage.getItem(STORAGE_KEY));
    const customer = ref<ClientCustomerPayload | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const otpSent = ref(false);
    const retryAfter = ref<number | null>(null);

    const isAuthenticated = computed(() => !!token.value);

    function resetCheckoutOtpState(): void {
        otpSent.value = false;
        error.value = null;
    }

    async function hydrateCustomer(): Promise<void> {
        if (!token.value) {
            return;
        }
        try {
            const data = await apiJson<ClientCustomerPayload>(
                'GET',
                apiUrl('auth/customer/me'),
                undefined,
                token.value,
            );
            customer.value = data;
        } catch (e: unknown) {
            const status = (e as { status?: number })?.status;
            if (status === 401 || status === 403) {
                logout();
            } else {
                customer.value = null;
            }
        }
    }

    /** Garante `customer` preenchido após há token — evita navegações no checkout antes do hydrate. */
    async function ensureCustomerLoaded(): Promise<void> {
        if (!token.value) {
            customer.value = null;
            return;
        }
        if (customer.value) {
            return;
        }
        await hydrateCustomer();
    }

    /** @param phone formato livre ou só dígitos — enviado como recebido; prefira dígitos BR no caller. */
    async function sendOtp(phone: string, opts?: SendOtpOptions): Promise<boolean> {
        const meta = useAppMetaStore();
        loading.value = true;
        error.value = null;
        try {
            const body: Record<string, string> = {
                phone,
                tenant_id: meta.tenantId,
            };
            const emailTrim = opts?.email?.trim();
            if (emailTrim) {
                body.email = emailTrim;
            }
            await apiJson('POST', apiUrl('auth/customer/otp/send'), body);
            otpSent.value = true;
            return true;
        } catch {
            error.value = opts?.errorMessageSend ?? 'Erro ao enviar código. Tente novamente.';
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function verifyOtp(
        phone: string,
        code: string,
        extras?: VerifyOtpExtras | null,
    ): Promise<{ is_new: boolean; customer: ClientCustomerPayload }> {
        const meta = useAppMetaStore();
        loading.value = true;
        error.value = null;
        try {
            const body: Record<string, string> = {
                phone,
                code,
                tenant_id: meta.tenantId,
            };
            const nameTrim = extras?.name?.trim();
            if (nameTrim) {
                body.name = nameTrim;
            }
            const emailTrim = extras?.email?.trim();
            if (emailTrim) {
                body.email = emailTrim;
            }
            const res = await apiJson<{
                token: string;
                is_new: boolean;
                customer: ClientCustomerPayload;
            }>('POST', apiUrl('auth/customer/otp/verify'), body);
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            customer.value = res.customer;
            return { is_new: res.is_new, customer: res.customer };
        } catch (e: unknown) {
            let msg = 'Código inválido ou expirado.';
            if (typeof e === 'object' && e !== null && 'status' in e) {
                const d = (e as { data?: { errors?: Record<string, string[]>; message?: string } }).data;
                const emailMsgs = (e as { status?: number }).status === 422 ? d?.errors?.email : undefined;
                if (Array.isArray(emailMsgs) && typeof emailMsgs[0] === 'string') {
                    msg = emailMsgs[0];
                } else if (typeof d?.message === 'string') {
                    // Cobre qualquer status com mensagem própria do backend (ex.: 403
                    // "Acesso temporariamente suspenso" de cliente bloqueado) — antes só
                    // 422 lia isso, e outros status caíam sempre no texto genérico de
                    // código incorreto, mascarando o motivo real do erro.
                    msg = d.message;
                }
            }
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    function extractAuthErrorMessage(e: unknown, fallback: string): string {
        if (typeof e === 'object' && e !== null && 'data' in e) {
            const d = (e as { data?: AuthErrorPayload }).data;
            const firstFieldError = d?.errors ? Object.values(d.errors)[0]?.[0] : undefined;
            if (typeof firstFieldError === 'string') {
                return firstFieldError;
            }
            if (typeof d?.message === 'string') {
                return d.message;
            }
        }
        return fallback;
    }

    async function register(
        payload: RegisterPayload,
    ): Promise<{ is_new: boolean; customer: ClientCustomerPayload }> {
        const meta = useAppMetaStore();
        loading.value = true;
        error.value = null;
        try {
            const body: Record<string, string> = {
                name: payload.name,
                phone: payload.phone,
                email: payload.email,
                password: payload.password,
                tenant_id: meta.tenantId,
            };
            if (payload.birth_date) {
                body.birth_date = payload.birth_date;
            }
            const res = await apiJson<{
                token: string;
                is_new: boolean;
                customer: ClientCustomerPayload;
            }>('POST', apiUrl('auth/customer/register'), body);
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            customer.value = res.customer;
            return { is_new: res.is_new, customer: res.customer };
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Não foi possível concluir o cadastro. Tente novamente.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    async function login(email: string, password: string): Promise<{ is_new: boolean; customer: ClientCustomerPayload }> {
        const meta = useAppMetaStore();
        loading.value = true;
        error.value = null;
        try {
            const res = await apiJson<{
                token: string;
                is_new: boolean;
                customer: ClientCustomerPayload;
            }>('POST', apiUrl('auth/customer/login'), {
                email,
                password,
                tenant_id: meta.tenantId,
            });
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            customer.value = res.customer;
            return { is_new: res.is_new, customer: res.customer };
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Email ou senha inválidos.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Domínio raiz do handoff OAuth (`config('app.pwa_base_domain')` no backend).
     * Preferência: `VITE_PWA_BASE_DOMAIN` (necessário quando o tenant tem domínio
     * próprio, onde não dá pra derivar do host atual). Fallback: remove o primeiro
     * label do host atual (`slug.borabeber.local` → `borabeber.local`) — só funciona
     * no subdomínio padrão, não em domínio próprio.
     */
    function resolvePwaRootDomain(): string {
        const fromEnv = (import.meta.env.VITE_PWA_BASE_DOMAIN as string | undefined)?.trim();
        if (fromEnv) {
            return fromEnv;
        }
        const parts = window.location.hostname.split('.');
        return parts.length > 2 ? parts.slice(1).join('.') : window.location.hostname;
    }

    /**
     * Redireciona pro handoff OAuth no domínio raiz — nunca retorna (troca de
     * página). `redirectTo` (ex.: "/checkout/address") atravessa o round-trip
     * inteiro via sessão do lado do servidor (GoogleAuthController) e volta
     * na query string do /auth/google/complete, pro GoogleCallback.vue saber
     * pra onde mandar o cliente depois de logar.
     */
    function loginWithGoogle(redirectTo?: string): void {
        const meta = useAppMetaStore();
        const rootDomain = resolvePwaRootDomain();
        const port = window.location.port ? `:${window.location.port}` : '';
        let url = `${window.location.protocol}//${rootDomain}${port}/auth/customer/google/redirect?tenant=${encodeURIComponent(meta.slug)}`;
        if (redirectTo) {
            url += `&redirect=${encodeURIComponent(redirectTo)}`;
        }
        window.location.href = url;
    }

    async function exchangeGoogleCode(code: string): Promise<{ is_new: boolean; customer: ClientCustomerPayload }> {
        loading.value = true;
        error.value = null;
        try {
            const res = await apiJson<{
                token: string;
                is_new: boolean;
                customer: ClientCustomerPayload;
            }>('POST', apiUrl('auth/customer/google/exchange'), { code });
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            customer.value = res.customer;
            return { is_new: res.is_new, customer: res.customer };
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Não foi possível concluir o login com Google.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    async function forgotPassword(email: string): Promise<void> {
        const meta = useAppMetaStore();
        loading.value = true;
        error.value = null;
        try {
            await apiJson('POST', apiUrl('auth/customer/password/forgot'), {
                email,
                tenant_id: meta.tenantId,
            });
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Não foi possível enviar o link de redefinição.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    async function resetPasswordWithToken(payload: {
        token: string;
        email: string;
        tenantId: string;
        password: string;
        passwordConfirmation: string;
    }): Promise<{ is_new: boolean; customer: ClientCustomerPayload }> {
        loading.value = true;
        error.value = null;
        try {
            const res = await apiJson<{
                token: string;
                is_new: boolean;
                customer: ClientCustomerPayload;
            }>('POST', apiUrl('auth/customer/password/reset'), {
                token: payload.token,
                email: payload.email,
                tenant_id: payload.tenantId,
                password: payload.password,
                password_confirmation: payload.passwordConfirmation,
            });
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            customer.value = res.customer;
            return { is_new: res.is_new, customer: res.customer };
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Link inválido ou expirado.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    async function patchCustomerProfile(payload: {
        name: string;
        email?: string | null;
        birth_date?: string | null;
        phone?: string;
    }): Promise<ClientCustomerPayload> {
        if (!token.value) {
            throw new Error('not authenticated');
        }
        loading.value = true;
        error.value = null;
        try {
            const body: { name: string; email?: string | null; birth_date?: string | null; phone?: string } = {
                name: payload.name,
            };
            if (payload.email !== undefined) {
                body.email = payload.email;
            }
            if (payload.birth_date !== undefined) {
                body.birth_date = payload.birth_date;
            }
            if (payload.phone !== undefined) {
                body.phone = payload.phone;
            }
            const updated = await apiJson<ClientCustomerPayload>(
                'PATCH',
                apiUrl('auth/customer/me'),
                body,
                token.value,
            );
            customer.value = updated;
            return updated;
        } finally {
            loading.value = false;
        }
    }

    async function patchCustomerPassword(payload: {
        password: string;
        password_confirmation: string;
        current_password?: string;
    }): Promise<ClientCustomerPayload> {
        if (!token.value) {
            throw new Error('not authenticated');
        }
        loading.value = true;
        error.value = null;
        try {
            const body: { password: string; password_confirmation: string; current_password?: string } = {
                password: payload.password,
                password_confirmation: payload.password_confirmation,
            };
            if (payload.current_password !== undefined) {
                body.current_password = payload.current_password;
            }
            const updated = await apiJson<ClientCustomerPayload>(
                'PATCH',
                apiUrl('auth/customer/password'),
                body,
                token.value,
            );
            customer.value = updated;
            return updated;
        } catch (e: unknown) {
            const msg = extractAuthErrorMessage(e, 'Não foi possível salvar a senha. Tente novamente.');
            error.value = msg;
            throw new Error(msg);
        } finally {
            loading.value = false;
        }
    }

    function logout(): void {
        token.value = null;
        customer.value = null;
        otpSent.value = false;
        localStorage.removeItem(STORAGE_KEY);
    }

    return {
        token,
        customer,
        loading,
        error,
        otpSent,
        retryAfter,
        isAuthenticated,
        resetCheckoutOtpState,
        hydrateCustomer,
        ensureCustomerLoaded,
        sendOtp,
        verifyOtp,
        register,
        login,
        loginWithGoogle,
        exchangeGoogleCode,
        forgotPassword,
        resetPasswordWithToken,
        patchCustomerProfile,
        patchCustomerPassword,
        logout,
    };
});
